
/** @typedef {{ type: 'nmtoken', start: number, end: number, value: string }} NmtokenParsed */
/** @typedef {{ start: number, end: number, name: string, value: LiteralParsed | NmtokenParsed | VariableRefParsed }} OptionParsed */
/** @typedef {{ type: 'markup-end', start: number, end: number, name: string }} MarkupEndParsed */
/** @typedef {{ type: 'markup-start', start: number, end: number, name: string, options: OptionParsed[] }} MarkupStartParsed */
/** @typedef {{ type: 'expression', operand: LiteralParsed | VariableRefParsed | undefined, start: number, end: number, name: string, options: OptionParsed[] }} ExpressionParsed */
/** @typedef {{ type: 'variable', start: number, end: number, name: string }} VariableRefParsed */
/** @typedef {{ type: 'literal', start: number, end: number, value: string }} LiteralParsed */
/** @typedef {{ type: 'junk', start: number, end: number, source: string }} JunkParsed */
/** @typedef {{ type: 'placeholder', start: number, end: number, body: LiteralParsed | VariableRefParsed | ExpressionParsed | MarkupStartParsed | MarkupEndParsed | JunkParsed }} PlaceholderParsed */
/** @typedef {{ type: 'text', start: number, end: number, value: string }} TextParsed */
/** @typedef {{ start: number, end: number, body: Array<TextParsed | PlaceholderParsed> }} PatternParsed */
/** @typedef {{ type: '*', start: number, end: number }} CatchallKeyParsed */
/** @typedef {{ start: number, end: number, keys: Array<LiteralParsed | NmtokenParsed | CatchallKeyParsed>, value: PatternParsed }} VariantParsed */
/** @typedef {{ start: number, end: number, target: VariableRefParsed | JunkParsed, value: PlaceholderParsed | JunkParsed }} DeclarationParsed */
/** @typedef {{ type: 'junk', declarations: DeclarationParsed[], source: string, errors: MessageSyntaxError[] }} JunkMessageParsed */
/** @typedef {{ type: 'select', declarations: DeclarationParsed[], selectors: PlaceholderParsed[], variants: VariantParsed[], errors: MessageSyntaxError[] }} SelectMessageParsed */
/** @typedef {{ type: 'message', declarations: DeclarationParsed[], pattern: PatternParsed, errors: MessageSyntaxError[] }} PatternMessageParsed */
/** @typedef {PatternMessageParsed | SelectMessageParsed | JunkMessageParsed} MessageParsed */
/** @typedef {Record<string, string>} Meta */

/* eslint-disable no-labels */

/**
 * Utility function for custom functions.
 * Cast resolved option values `'true'` and `'false'` to their Boolean equivalents.
 * Any other values are untouched.
 *
 * @beta
 * @param options Options object, which may be modified.
 * @param names Names of options that should have boolean values.
 */
function castAsBoolean (options, ...names) {
  for (const name of names) {
    const value = options[name]
    if (typeof value === 'string') {
      if (value === 'true') options[name] = true
      else if (value === 'false') options[name] = false
    }
  }
}

/**
 * Utility function for custom functions.
 * Cast resolved option values with numerical string representations of integers
 * to their Number equivalents. Any other values are untouched.
 *
 * @beta
 * @param options Options object, which may be modified.
 * @param names Names of options that should have integer values.
 */
function castAsInteger (options, ...names) {
  for (const name of names) {
    const value = options[name]
    if (typeof value === 'string') {
      const num = Number(value)
      if (Number.isInteger(num)) options[name] = num
    }
  }
}

// ERRORS
export class MessageError extends Error {
  constructor (type, message) {
    super(message)
    this.type = type
  }
}

export class MessageSyntaxError extends MessageError {
  constructor (type, start, end, char) {
    let message
    switch (type) {
      case 'empty-token':
      case 'bad-escape':
      case 'bad-local-var':
      case 'bad-selector':
      case 'extra-content':
      case 'parse-error':
        message = `Syntax parse error: ${type} at ${start}`
        break
      case 'missing-char':
        message = `Syntax parse error: Missing character ${char} at ${start}`
        break
      case 'key-mismatch':
      case 'missing-fallback':
        message = `Data model error: ${type}`
        if (start >= 0) message += ` at ${start}`
    }
    super(type, message)
    this.start = start
    this.end = end
  }
}

class MissingCharError extends MessageSyntaxError {
  constructor (pos, char) {
    super('missing-char', pos, pos + 1, char)
    this.char = char
  }
}

class MessageDataModelError extends MessageSyntaxError {
  type
}

class MessageParser {
  #src
  #offset = 0

  constructor (src) {
    this.#src = src
  }

  // Message ::= Declaration* ( Pattern | Selector Variant+ )
  // Selector ::= 'match' ( '{' Expression '}' )+
  parse () {
    const errors = []
    const { declarations, end: pos } = this.parseDeclarations(errors)

    if (this.#src.startsWith('match', pos)) {
      return this.parseSelectMessage(declarations, errors)
    } else if (this.#src[pos] === '{') {
      return this.parsePatternMessage(declarations, errors)
    } else {
      errors.push(new MessageSyntaxError('parse-error', pos, this.#src.length))
      return { type: 'junk', declarations, errors, source: this.#src }
    }
  }

  /**
   *
   * @param {MessageSyntaxError[]} errors
   * @returns {{ declarations: DeclarationParsed[], end: number }}
   */
  parseDeclarations (errors) {
    this.#offset = whitespaces(this.#src, 0)
    /** @type {DeclarationParsed[]} */
    const declarations = []
    while (this.#src.startsWith('let', this.#offset)) {
      const start = this.#offset
      this.#offset += 3 // 'let'
      const ws = whitespaces(this.#src, this.#offset)
      this.#offset += ws

      if (ws === 0) errors.push(new MissingCharError(this.#offset, ' '))

      /** @type {VariableRefParsed | JunkParsed} */
      let target
      if (this.#src[this.#offset] === '$') {
        target = parseVariable(this.#src, this.#offset, errors)
        this.#offset = target.end
      } else {
        const junkStart = this.#offset
        const junkEndOffset = this.#src
          .substring(this.#offset)
          .search(/[\t\n\r ={}]/)
        this.#offset =
          junkEndOffset === -1 ? this.#src.length : this.#offset + junkEndOffset
        target = {
          type: 'junk',
          start: junkStart,
          end: this.#offset,
          source: this.#src.substring(junkStart, this.#offset)
        }
        errors.push(new MissingCharError(junkStart, '$'))
      }

      this.#offset += whitespaces(this.#src, this.#offset)
      if (this.#src[this.#offset] === '=') this.#offset += 1
      else errors.push(new MissingCharError(this.#offset, '='))

      /** @type {PlaceholderParsed | JunkParsed} */
      let value
      this.#offset += whitespaces(this.#src, this.#offset)
      if (this.#src[this.#offset] === '{') {
        value = parsePlaceholder(this.#src, this.#offset, errors)
        this.#offset = value.end
      } else {
        const junkStart = this.#offset
        const junkEndOffset = this.#src
          .substring(this.#offset)
          .search(/\blet|\bmatch|\bwhen|[${}]/)
        this.#offset =
          junkEndOffset === -1 ? this.#src.length : this.#offset + junkEndOffset
        value = {
          type: 'junk',
          start: junkStart,
          end: this.#offset,
          source: this.#src.substring(junkStart, this.#offset)
        }
        errors.push(new MissingCharError(junkStart, '{'))
      }
      declarations.push({ start, end: this.#offset, target, value })
      this.incrementWhitespace()
    }
    checkLocalVarReferences(declarations, errors)
    return { declarations, end: this.#offset }
  }

  /**
   *
   * @param {string} src
   * @param {number} start
   * @param {DeclarationParsed[]} declarations
   * @param {MessageSyntaxError[]} errors
   * @returns {SelectMessageParsed}
   */
  parseSelectMessage (declarations, errors) {
    // let pos = start + 5 // 'match'
    this.#offset += 5
    this.incrementWhitespace()

    /** @type {PlaceholderParsed[]} */
    const selectors = []
    while (this.#src[this.#offset] === '{') {
      const ph = parsePlaceholder(this.#src, this.#offset, errors)
      switch (ph.body.type) {
        case 'expression':
        case 'literal':
        case 'variable':
          break
        default: {
          const { start, end } = ph.body
          errors.push(new MessageSyntaxError('bad-selector', start, end))
        }
      }
      selectors.push(ph)
      this.#offset = ph.end
      this.incrementWhitespace()
    }
    if (selectors.length === 0) {
      errors.push(
        new MessageSyntaxError('empty-token', this.#offset, this.#offset + 1)
      )
    }

    /** @type {VariantParsed[]} */
    const variants = []
    this.incrementWhitespace()
    while (this.#src.startsWith('when', this.#offset)) {
      const variant = parseVariant(
        this.#src,
        this.#offset,
        selectors.length,
        errors
      )
      variants.push(variant)
      this.#offset = variant.end
      this.incrementWhitespace()
    }

    if (this.#offset < this.#src.length) {
      errors.push(
        new MessageSyntaxError(
          'extra-content',
          this.#offset,
          this.#offset.length
        )
      )
    }

    return { type: 'select', declarations, selectors, variants, errors }
  }

  /**
   *
   * @param {DeclarationParsed[]} declarations
   * @param {MessageSyntaxError[]} errors
   * @returns {PatternMessageParsed}
   */
  parsePatternMessage (declarations, errors) {
    const pattern = parsePattern(this.#src, this.#offset, errors)
    this.#offset = pattern.end
    this.incrementWhitespace()

    if (this.#offset < this.#src.length) {
      errors.push(
        new MessageSyntaxError('extra-content', this.#offset, this.#src.length)
      )
    }

    return { type: 'message', declarations, pattern, errors }
  }

  incrementWhitespace () {
    this.#offset += whitespaces(this.#src, this.#offset)
    return this
  }
}

const FALLBACK_SOURCE = '�'
const hasMeta = part =>
  !!part.meta &&
  typeof part.meta === 'object' &&
  Object.keys(part.meta).length > 0
const isLocaleContext = lc =>
  !!lc &&
  typeof lc === 'object' &&
  Array.isArray(lc.locales) &&
  typeof lc.locales[0] === 'string'

class MessageValue {
  #type

  #localeContext

  constructor (type, locale, value, format) {
    this.#type = type ?? 'value'
    this.value = value
    this.#localeContext = locale
    if (format) {
      const { meta, source, toString } = format
      if (source) this.source = source
      if (meta) this.meta = { ...meta }
      if (
        toString &&
        Object.prototype.hasOwnProperty.call(format, 'toString')
      ) {
        this.toString = toString
      }
    }
  }

  get type () {
    return this.#type
  }

  get localeContext () {
    let localeMatcher
    let locales
    const lc = this.#localeContext
    if (lc && typeof lc === 'string') locales = [lc]
    else if (Array.isArray(lc)) locales = lc.slice()
    else if (isLocaleContext(lc)) {
      locales = lc.locales.slice()
      localeMatcher = lc.localeMatcher
    }
    return locales ? { locales, localeMatcher } : null
  }

  set localeContext (locale) {
    this.#localeContext = locale
  }

  /**
   *
   * @param {string} key
   * @returns {boolean | Meta}
   */
  matchSelectKey (key) {
    return this.value != null && String(this.value) === key
  }

  /**
   *
   * @param {Context['onError']} onError
   * @returns {string}
   */
  toString (onError) {
    const value = this.value
    try {
      const lc = this.localeContext
      if (lc && value && typeof value.toLocaleString === 'function') {
        const lm = lc.localeMatcher
        const opt = lm ? { localeMatcher: lm } : undefined
        return value.toLocaleString(lc.locales, opt)
      }
    } catch (error) {
      if (onError) onError(error, this)
      if (value === undefined) {
        const source = this.source || FALLBACK_SOURCE
        return `{${source}}`
      }
    }
    return String(value)
  }
}

class MessageDateTime extends MessageValue {
  options

  constructor (locale, date, { meta, options, source } = {}) {
    const fmt = { meta, source }
    if (date instanceof MessageDateTime) {
      const lc = extendLocaleContext(date.localeContext, locale)
      super('datetime', lc, date.value, fmt)
      if (options || date.options)
        this.options = date.options
          ? { ...date.options, ...options }
          : { ...options }
    } else {
      if (typeof date === 'number') date = new Date(date)
      if (date instanceof Date) {
        super('datetime', locale, date, fmt)
        if (options) this.options = { ...options }
      } else throw new TypeError(`Invalid ${typeof date} as date argument`)
    }
  }

  #getDateTimeFormatter () {
    const lc = this.localeContext
    const opt = lc
      ? { localeMatcher: lc.localeMatcher, ...this.options }
      : this.options
    return new Intl.DateTimeFormat(lc?.locales, opt)
  }

  toParts () {
    const dtf = this.#getDateTimeFormatter()
    return dtf.formatToParts(this.value)
  }

  toString (onError) {
    try {
      const hasOpt = this.options && Object.keys(this.options).length > 0
      if (hasOpt) {
        const dtf = this.#getDateTimeFormatter()
        return dtf.format(this.value)
      } else {
        const lc = this.localeContext
        return lc
          ? this.value.toLocaleString(lc.locales, lc)
          : String(this.value)
      }
    } catch (error) {
      if (onError) onError(error, this)
      if (this.value === undefined) {
        const source = this.source || FALLBACK_SOURCE
        return `{${source}}`
      }
      return String(this.value)
    }
  }
}

class MessageLiteral extends MessageValue {
  constructor (literal, fmt) {
    super('literal', null, literal, fmt)
  }

  toString () {
    return this.value
  }
}

class MessageFallback extends MessageValue {
  constructor (locale, fmt) {
    super('fallback', locale, undefined, fmt)
  }

  matchSelectKey () {
    return false
  }

  toString () {
    const source = this.source || FALLBACK_SOURCE
    return `{${source}}`
  }
}

class MessageMarkupStart extends MessageValue {
  options

  constructor (locale, name, { meta, options, source }) {
    super('markup-start', locale, name, { meta, source })
    this.options = { ...options }
  }

  matchSelectKey () {
    return false
  }

  toString () {
    return `{+${this.value}}`
  }
}

class MessageMarkupEnd extends MessageValue {
  constructor (locale, name, options) {
    super('markup-end', locale, name, options)
  }

  matchSelectKey () {
    return false
  }

  toString () {
    return `{-${this.value}}`
  }
}

class MessageNumber extends MessageValue {
  options

  constructor (locale, number, { meta, options, source } = {}) {
    const fmt = { meta, source }
    if (number instanceof MessageNumber) {
      const lc = extendLocaleContext(number.localeContext, locale)
      super('number', lc, number.value, fmt)
      if (options || number.options)
        this.options = number.options
          ? { ...number.options, ...options }
          : { ...options }
    } else if (typeof number === 'number' || typeof number === 'bigint') {
      super('number', locale, number, fmt)
      if (options) this.options = { ...options }
    } else throw TypeError(`Invalid ${typeof number} as number argument`)
  }

  /**
   *
   * @param {typeof Intl.NumberFormat | typeof Intl.PluralRules} Class
   * @returns
   */
  #getIntl (Class) {
    const lc = this.localeContext
    const lm = lc?.localeMatcher
    const opt = lm ? { localeMatcher: lm, ...this.options } : this.options
    return new Class(lc?.locales, opt)
  }

  #getPluralCategory () {
    if (!this.localeContext) return 'other'
    const pr = this.#getIntl(Intl.PluralRules)
    // Intl.PluralRules really does need a number
    const num = Number(this.value)
    return pr.select(num)
  }

  /**
   * In addition to matching exact values,
   * numerical values will also match keys with the same plural rule category,
   * i.e. one of `zero`, `one`, `two`, `few`, `many`, and `other`.
   *
   * @remarks
   * Different languages use different subset of plural rule categories.
   * For example, cardinal English plurals only use `one` and `other`,
   * so a key `zero` will never be matched for that locale.
   */
  matchSelectKey (key) {
    if (/^[0-9]+$/.test(key) && key === String(this.value)) return true
    if (key === this.#getPluralCategory()) return { plural: key }
    return false
  }

  /**
   *
   * @returns {Intl.NumberFormatPart[]}
   */
  toParts () {
    const nf = this.#getIntl(Intl.NumberFormat)
    return nf.formatToParts(this.value)
  }

  toString (onError) {
    try {
      const nf = this.#getIntl(Intl.NumberFormat)
      return nf.format(this.value)
    } catch (error) {
      if (onError) onError(error, this)
      if (this.value === undefined) {
        const source = this.source || FALLBACK_SOURCE
        return `{${source}}`
      }
      return String(this.value)
    }
  }
}

class ResolvedMessage extends MessageValue {
  // Cache for string value
  #str

  constructor (context, message, source) {
    const { meta, pattern } = getPattern(context, message)
    const resMsg = pattern.map(elem => context.resolve(elem))
    super('message', context, resMsg, { meta, source })
  }

  matchSelectKey (key) {
    return this.toString() === key
  }

  toString (onError) {
    if (typeof this.#str !== 'string') {
      this.#str = ''
      for (const mv of this.value) this.#str += mv.toString(onError)
    }
    return this.#str
  }
}

const defaultRuntime = {
  datetime: (locales, options, arg) => {
    let date
    if (!arg) date = new Date()
    else if (arg instanceof MessageDateTime) date = arg
    else if (typeof arg.value === 'number') date = new Date(arg.value)
    else date = new Date(String(arg.value))
    castAsBoolean(options, 'hour12')
    castAsInteger(options, 'fractionalSecondDigits')
    return new MessageDateTime(locales, date, { options })
  },
  number: (locales, options, arg) => {
    const num = arg instanceof MessageNumber ? arg : Number(arg?.value)
    castAsBoolean(options, 'useGrouping')
    castAsInteger(
      options,
      'minimumIntegerDigits',
      'minimumFractionDigits',
      'maximumFractionDigits',
      'minimumSignificantDigits',
      'maximumSignificantDigits'
    )
    return new MessageNumber(locales, num, { options })
  }
}

/**
 * Create a new message formatter.
 *
 * @beta
 */
export default class MessageFormat {
  /** @type {'best fit' | 'lookup'} */
  #localeMatcher
  /** @type {string[]} */
  #locales
  /** @type {Message} */
  #message
  /** @type {Readonly<Runtime>} */
  #runtime

  /**
   *
   * @param {Map<string, string | MessageFormat>} resource
   * @param {string | string[] | undefined} locales
   * @param {MessageFormatOptions | undefined} options
   */
  static parseResource (resource, locales, options) {
    throw new Error('static MessageFormat.parseResource is not yet implemented.')
  }

  /**
   *
   * @param {string | Message} source
   * @param {string | string[] | undefined} locales
   * @param {MessageFormatOptions | undefined} options
   */
  constructor (source, locales, options) {
    this.#localeMatcher = options?.localeMatcher ?? 'best fit'
    this.#locales = Array.isArray(locales)
      ? locales.slice()
      : locales
        ? [locales]
        : []
    this.#message =
      typeof source === 'string' ? new MessageParser(source).parse() : source
    const rt = options?.runtime ?? defaultRuntime
    this.#runtime = Object.freeze({ ...rt })
  }

  /**
   *
   * @param {Record<string, unknown> | undefined} msgParams
   * @param {(error: unknown, value: MessageValue | undefined) => void | undefined} onError
   * @returns {ResolvedMessage}
   */
  resolveMessage (msgParams, onError) {
    if (onError && this.#message.errors) {
      for (const error of this.#message.errors) onError(error, undefined)
    }
    const ctx = this.#createContext(msgParams, onError)
    return new ResolvedMessage(ctx, this.#message)
  }

  resolvedOptions () {
    return {
      localeMatcher: this.#localeMatcher,
      locales: this.#locales.slice(),
      message: this.#message,
      runtime: this.#runtime
    }
  }

  /**
   *
   * @param {Context['scope']} scope
   * @param {Context['onError']} onError
   * @returns
   */
  #createContext (
    scope = {},
    onError = () => {
      // Ignore errors by default
    }
  ) {
    const { declarations } = this.#message
    /** @type {Context} */
    const ctx = {
      onError,
      resolve: elem => resolvePatternElement(ctx, elem),
      declarations,
      localeMatcher: this.#localeMatcher,
      locales: this.#locales,
      runtime: this.#runtime,
      // If declarations exist, scope may be modified during formatting
      scope: declarations.length > 0 ? { ...scope } : scope
    }
    return ctx
  }
}

function extendLocaleContext (orig, lc) {
  let { locales, localeMatcher } = orig ?? {}

  if (lc && typeof lc === 'string') {
    locales ??= [lc]
  } else if (Array.isArray(lc)) {
    locales ??= lc.slice()
  } else if (isLocaleContext(lc)) {
    locales ??= lc.locales.slice()
    localeMatcher ??= lc.localeMatcher
  }

  return locales ? { locales, localeMatcher } : null
}

function getPattern (context, message) {
  switch (message.type) {
    case 'message':
      return { pattern: message.pattern.body }

    case 'select': {
      const resSelectors = message.selectors.map(sel => context.resolve(sel))

      cases: for (const { keys, value } of message.variants) {
        let meta = undefined
        for (let i = 0; i < keys.length; ++i) {
          const key = keys[i]
          const rs = resSelectors[i]
          const match = key.type === '*' || rs?.matchSelectKey(key.value)
          if (!match) continue cases
          if (typeof match === 'object') meta = Object.assign(meta ?? {}, match)
        }
        return { pattern: value.body, meta }
      }

      return { pattern: [], meta: { selectResult: 'no-match' } }
    }

    default:
      return { pattern: [{ type: 'junk', source: message.source }] }
  }
}

// RESOLVE

/**
 *
 * @param {Context} ctx
 * @param {PatternElement} elem
 * @returns {MessageValue}
 */
function resolvePatternElement (ctx, elem) {
  switch (elem.type) {
    case 'literal':
    case 'nmtoken':
    case 'text':
      return new MessageLiteral(elem.value)
    case 'placeholder':
      return resolvePatternElement(ctx, elem.body)

    case 'variable': {
      const { name } = elem
      const source = '$' + name
      let value
      const decl = ctx.declarations.find(decl => decl.target.name === name)
      if (decl) {
        value = ctx.resolve(decl.value)
        ctx.scope[name] = value
      } else {
        value = getValue(ctx.scope, name)
      }
      if (value !== undefined) return asMessageValue(ctx, value, { source })

      const fb = new MessageFallback(ctx, { source })
      ctx.onError(
        new MessageError('unresolved-var', `Variable not available: ${source}`),
        fb
      )
      return fb
    }

    case 'expression': {
      const { operand, name, options } = elem
      let source
      const rf = ctx.runtime[name]
      try {
        let fnArgs
        if (operand) {
          const arg = ctx.resolve(operand)
          fnArgs = [arg]
          source =
            arg.source ||
            String((arg.type === 'literal' && arg.value) || FALLBACK_SOURCE)
        } else {
          fnArgs = []
          source = `:${name}`
        }
        if (!rf) {
          throw new MessageError('missing-func', `Unknown function ${name}`)
        }

        const opt = { localeMatcher: ctx.localeMatcher }
        if (options) {
          for (const { name, value } of options) {
            opt[name] = ctx.resolve(value).value
          }
        }
        const res = rf(ctx.locales, opt, ...fnArgs)
        return asMessageValue(ctx, res, { source })
      } catch (error) {
        source ??= operand ? FALLBACK_SOURCE : `:${name}`
        const fb = new MessageFallback(ctx, { source })
        ctx.onError(error, fb)
        return fb
      }
    }

    case 'markup-start': {
      const { name, options } = elem
      const source = `+${name}`
      try {
        return new MessageMarkupStart(ctx, name, {
          options: resolveOptions(ctx, options),
          source
        })
      } catch (error) {
        const fb = new MessageFallback(ctx, { source })
        ctx.onError(error, fb)
        return fb
      }
    }

    case 'markup-end': {
      const { name } = elem
      return new MessageMarkupEnd(ctx, elem.name, { source: `-${elem.name}` })
    }

    case 'junk': {
      const source = elem
      const fb = new MessageFallback(ctx, { source })
      ctx.onError(
        new MessageError(
          'junk-element',
          'Junk pattern element cannot be resolved'
        ),
        fb
      )
      return fb
    }
    default:
      // @ts-expect-error - should never happen
      throw new Error(`Unsupported pattern element: ${elem.type}`)
  }
}

function asMessageValue (ctx, value, format) {
  if (value instanceof MessageValue) {
    if (format?.meta) value.meta = { ...value.meta, ...format.meta }
    if (format?.source) value.source = format.source
    return value
  }

  if (typeof value === 'number' || typeof value === 'bigint')
    return new MessageNumber(ctx, value, format)

  if (value instanceof Date) return new MessageDateTime(ctx, value, format)

  return new MessageValue(null, ctx, value, format)
}

function resolveOptions (ctx, options) {
  const opt = {}
  if (options) {
    for (const { name, value } of options) {
      opt[name] = ctx.resolve(value).value
    }
  }
  return opt
}

const isScope = scope =>
  scope instanceof Object && !(scope instanceof MessageValue)
/**
 * Looks for the longest matching `.` delimited starting substring of name.
 * @returns `undefined` if value not found
 */
function getValue (scope, name) {
  if (isScope(scope)) {
    if (name in scope) return scope[name]

    const parts = name.split('.')
    for (let i = parts.length - 1; i > 0; --i) {
      const head = parts.slice(0, i).join('.')
      if (head in scope) {
        const tail = parts.slice(i).join('.')
        return getValue(scope[head], tail)
      }
    }
  }

  return undefined
}

// Variant ::= 'when' ( WhiteSpace VariantKey )+ Pattern
// VariantKey ::= Literal | Nmtoken | '*'
/**
 *
 * @param {string} src
 * @param {number} start
 * @param {number} selCount
 * @param {MessageSyntaxError[]} errors
 * @returns {VariantParsed}
 */
function parseVariant (src, start, selCount, errors) {
  let pos = start + 4 // 'when'
  /** @type {Array<LiteralParsed | NmtokenParsed | CatchallKeyParsed>} */
  const keys = []
  while (pos < src.length) {
    const ws = whitespaces(src, pos)
    pos += ws
    const ch = src[pos]
    if (ch === '{') break

    if (ws === 0) errors.push(new MissingCharError(pos, ' '))

    /** @type {CatchallKeyParsed | LiteralParsed | NmtokenParsed} */
    let key
    switch (ch) {
      case '*':
        key = { type: '*', start: pos, end: pos + 1 }
        break
      case '|':
        key = parseLiteral(src, pos, errors)
        break
      default:
        key = parseNmtoken(src, pos, errors)
    }
    if (key.end === pos) break // error; reported in pattern.errors
    keys.push(key)
    pos = key.end
  }

  if (selCount > 0 && keys.length !== selCount) {
    const end = keys.length === 0 ? pos : keys[keys.length - 1].end
    errors.push(new MessageDataModelError('key-mismatch', start, end))
  }

  const value = parsePattern(src, pos, errors)
  return { start, end: value.end, keys, value }
}

// Pattern ::= '{' (Text | Placeholder)* '}' /* ws: explicit */
/**
 *
 * @param {string} src
 * @param {number} start
 * @param {MessageSyntaxError[]} errors
 * @returns {PatternParsed}
 */
function parsePattern (src, start, errors) {
  if (src[start] !== '{') {
    errors.push(new MissingCharError(start, '{'))
    return { start, end: start, body: [] }
  }

  let pos = start + 1
  /** @type {Array<TextParsed | PlaceholderParsed>} */
  const body = []
  loop: while (pos < src.length) {
    switch (src[pos]) {
      case '{': {
        const ph = parsePlaceholder(src, pos, errors)
        body.push(ph)
        pos = ph.end
        break
      }
      case '}':
        break loop
      default: {
        const tx = parseText(src, pos, errors)
        body.push(tx)
        pos = tx.end
      }
    }
  }

  if (src[pos] === '}') pos += 1
  else errors.push(new MissingCharError(pos, '}'))

  return { start, end: pos, body }
}

// DECLARATIONS
// Declaration ::= 'let' WhiteSpace Variable '=' '{' Expression '}'
/**
 *
 * @param {string} src
 * @param {MessageSyntaxError[]} errors
 * @returns {{ declarations: DeclarationParsed[], end: number }}
 */
export function parseDeclarations (src, errors) {
  let pos = whitespaces(src, 0)
  /** @type {DeclarationParsed[]} */
  const declarations = []
  while (src.startsWith('let', pos)) {
    const decl = parseDeclaration(src, pos, errors)
    declarations.push(decl)
    pos = decl.end
    pos += whitespaces(src, pos)
  }
  checkLocalVarReferences(declarations, errors)
  return { declarations, end: pos }
}

/**
 *
 * @param {string} src
 * @param {number} start
 * @param {MessageSyntaxError[]} errors
 * @returns {DeclarationParsed}
 */
function parseDeclaration (src, start, errors) {
  let pos = start + 3 // 'let'
  const ws = whitespaces(src, pos)
  pos += ws

  if (ws === 0) errors.push(new MissingCharError(pos, ' '))

  /** @type {VariableRefParsed | JunkParsed} */
  let target
  if (src[pos] === '$') {
    target = parseVariable(src, pos, errors)
    pos = target.end
  } else {
    const junkStart = pos
    const junkEndOffset = src.substring(pos).search(/[\t\n\r ={}]/)
    pos = junkEndOffset === -1 ? src.length : pos + junkEndOffset
    target = {
      type: 'junk',
      start: junkStart,
      end: pos,
      source: src.substring(junkStart, pos)
    }
    errors.push(new MissingCharError(junkStart, '$'))
  }

  pos += whitespaces(src, pos)
  if (src[pos] === '=') pos += 1
  else errors.push(new MissingCharError(pos, '='))

  /** @type {PlaceholderParsed | JunkParsed} */
  let value
  pos += whitespaces(src, pos)
  if (src[pos] === '{') {
    value = parsePlaceholder(src, pos, errors)
    pos = value.end
  } else {
    const junkStart = pos
    const junkEndOffset = src
      .substring(pos)
      .search(/\blet|\bmatch|\bwhen|[${}]/)
    pos = junkEndOffset === -1 ? src.length : pos + junkEndOffset
    value = {
      type: 'junk',
      start: junkStart,
      end: pos,
      source: src.substring(junkStart, pos)
    }
    errors.push(new MissingCharError(junkStart, '{'))
  }

  return { start, end: pos, target, value }
}

/**
 * Local variable declarations can't refer to later ones.
 * @param {DeclarationParsed[]} declarations
 * @param {MessageSyntaxError[]} errors
 */
function checkLocalVarReferences (declarations, errors) {
  const check = (name, ref) => {
    if (ref.name === name) {
      errors.push(new MessageSyntaxError('bad-local-var', ref.start, ref.end))
    }
  }

  for (let i = 1; i < declarations.length; ++i) {
    const { name } = declarations[i].target
    if (!name) continue
    for (let j = 0; j < i; ++j) {
      const ph = declarations[j].value
      if (ph.type === 'placeholder') {
        const exp = ph.body
        switch (exp.type) {
          case 'expression':
            if (exp.operand?.type === 'variable') check(name, exp.operand)
            for (const opt of exp.options) {
              if (opt.value.type === 'variable') check(name, opt.value)
            }
            break
          case 'variable':
            check(name, exp)
            break
        }
      }
    }
  }
}

// PLACEHOLDERS
// Placeholder ::= '{' (Expression | Markup | MarkupEnd)? '}'
/**
 *
 * @param {string} src
 * @param {number} start
 * @param {MessageSyntaxError[]} errors
 * @returns {PlaceholderParsed}
 */
export function parsePlaceholder (src, start, errors) {
  let pos = start + 1 // '{'
  pos += whitespaces(src, pos)

  /** @type {LiteralParsed | VariableRefParsed | undefined} */
  let arg
  switch (src[pos]) {
    case '|':
      arg = parseLiteral(src, pos, errors)
      pos = arg.end
      break
    case '$': {
      arg = parseVariable(src, pos, errors)
      pos = arg.end
      break
    }
  }

  /** @type {LiteralParsed | VariableRefParsed | ExpressionParsed | MarkupStartParsed | MarkupEndParsed | JunkParsed} */
  let body
  /** @type {MessageSyntaxError | undefined} */
  let junkError
  pos += whitespaces(src, pos)
  switch (src[pos]) {
    case ':':
    case '+':
    case '-':
      body = parseExpressionOrMarkup(src, pos, arg, errors)
      pos = body.end
      break
    default:
      if (arg) {
        body = arg
      } else {
        const source = src.substring(pos, pos + 1)
        body = { type: 'junk', start: pos, end: pos, source }
        junkError = new MessageSyntaxError('parse-error', pos, pos + 1)
        errors.push(junkError)
      }
  }
  pos += whitespaces(src, pos)

  if (pos >= src.length) {
    errors.push(new MissingCharError(pos, '}'))
  } else if (src[pos] !== '}') {
    const errStart = pos
    while (pos < src.length && src[pos] !== '}') pos += 1
    if (body.type === 'junk') {
      body.end = pos
      body.source = src.substring(body.start, pos)
      if (junkError) junkError.end = pos
    } else {
      errors.push(new MessageSyntaxError('extra-content', errStart, pos))
    }
  } else {
    pos += 1
  }

  return { type: 'placeholder', start, end: pos, body }
}

// Expression ::= Operand Annotation? | Annotation
// Operand ::= Literal | Variable
// Annotation ::= Function Option*
// Function ::= ':' Name /* ws: explicit */
// Markup ::= MarkupStart Option*
// MarkupStart ::= '+' Name /* ws: explicit */
// MarkupEnd ::= '-' Name /* ws: explicit */
/**
 *
 * @param {string} src
 * @param {number} start
 * @param {LiteralParsed | VariableRefParsed | undefined} operand
 * @param {MessageSyntaxError[]} errors
 * @returns {ExpressionParsed | MarkupStartParsed | MarkupEndParsed}
 */
function parseExpressionOrMarkup (src, start, operand, errors) {
  const sigil = src[start]
  let pos = start + 1 // ':' | '+' | '-'
  const name = parseNameValue(src, pos)
  if (!name) errors.push(new MessageSyntaxError('empty-token', pos, pos + 1))
  /** @type {OptionParsed[]} */
  const options = []
  pos += name.length
  while (pos < src.length) {
    const ws = whitespaces(src, pos)
    if (src[pos + ws] === '}') break
    if (ws === 0) errors.push(new MissingCharError(pos, ' '))
    pos += ws
    const opt = parseOption(src, pos, errors)
    if (opt.end === pos) break // error
    options.push(opt)
    pos = opt.end
  }

  const end = pos
  if (sigil === ':') {
    return { type: 'expression', operand, start, end, name, options }
  }

  if (operand) {
    const { start, end } = operand
    errors.unshift(new MessageSyntaxError('extra-content', start, end))
  }
  if (sigil === '+') {
    return { type: 'markup-start', start, end, name, options }
  }

  if (options.length > 0) {
    errors.push(
      new MessageSyntaxError(
        'extra-content',
        options[0].start,
        options[options.length - 1].end
      )
    )
  }
  return { type: 'markup-end', start, end, name }
}

// Option ::= Name '=' (Literal | Nmtoken | Variable)
/**
 *
 * @param {string} src
 * @param {number} start
 * @param {MessageSyntaxError[]} errors
 * @returns {OptionParsed}
 */
function parseOption (src, start, errors) {
  const name = parseNameValue(src, start)
  let pos = start + name.length
  pos += whitespaces(src, pos)
  if (src[pos] === '=') pos += 1
  else errors.push(new MissingCharError(pos, '='))
  pos += whitespaces(src, pos)
  /** @type {LiteralParsed | NmtokenParsed | VariableRefParsed} */
  let value
  switch (src[pos]) {
    case '|':
      value = parseLiteral(src, pos, errors)
      break
    case '$':
      value = parseVariable(src, pos, errors)
      break
    default:
      value = parseNmtoken(src, pos, errors)
  }
  return { start, end: value.end, name, value }
}

// VALUES

// Text ::= (TextChar | TextEscape)+
// TextChar ::= AnyChar - ('{' | '}' | Esc)
// AnyChar ::= [#x0-#x10FFFF]
// Esc ::= '\'
// TextEscape ::= Esc Esc | Esc '{' | Esc '}'
/**
 *
 * @param {string} src
 * @param {number} start
 * @param {MessageSyntaxError[]} errors
 * @returns {TextParsed}
 */
export function parseText (src, start, errors) {
  let value = ''
  let pos = start
  let i = start
  loop: for (; i < src.length; ++i) {
    switch (src[i]) {
      case '\\': {
        const esc = src[i + 1]
        if (esc !== '\\' && esc !== '{' && esc !== '}') {
          errors.push(new MessageSyntaxError('bad-escape', i, i + 2))
        } else {
          value += src.slice(pos, i)
          i += 1
          pos = i
        }
        break
      }
      case '{':
      case '}':
        break loop
    }
  }
  value += src.slice(pos, i)
  return { type: 'text', start, end: i, value }
}

// Literal ::= '(' (LiteralChar | LiteralEscape)* ')' /* ws: explicit */
// Esc ::= '\'
// LiteralChar ::= AnyChar - ('|' | Esc)
// LiteralEscape ::= Esc Esc | Esc '|'
/**
 *
 * @param {string} src
 * @param {number} start
 * @param {MessageSyntaxError[]} errors
 * @returns {LiteralParsed}
 */
export function parseLiteral (src, start, errors) {
  let value = ''
  let pos = start + 1
  for (let i = pos; i < src.length; ++i) {
    switch (src[i]) {
      case '\\': {
        const esc = src[i + 1]
        if (esc !== '\\' && esc !== '|') {
          errors.push(new MessageSyntaxError('bad-escape', i, i + 2))
        } else {
          value += src.substring(pos, i)
          i += 1
          pos = i
        }
        break
      }
      case '|':
        value += src.substring(pos, i)
        return { type: 'literal', start, end: i + 1, value }
    }
  }
  value += src.substring(pos)
  errors.push(new MissingCharError(src.length, '|'))
  return { type: 'literal', start, end: src.length, value }
}

// Variable ::= '$' Name /* ws: explicit */
/**
 *
 * @param {string} src
 * @param {number} start
 * @param {MessageSyntaxError[]} errors
 * @returns {VariableRefParsed}
 */
export function parseVariable (src, start, errors) {
  const pos = start + 1
  const name = parseNameValue(src, pos)
  const end = pos + name.length
  if (!name) errors.push(new MessageSyntaxError('empty-token', pos, pos + 1))
  return { type: 'variable', start, end, name }
}

// NAMES

// NameStart ::= [a-zA-Z] | "_"
//             | [#xC0-#xD6] | [#xD8-#xF6] | [#xF8-#x2FF]
//             | [#x370-#x37D] | [#x37F-#x1FFF] | [#x200C-#x200D]
//             | [#x2070-#x218F]
//             | [#x2C00-#x2FEF]
//             | [#x3001-#xD7FF]
//             | [#xF900-#xFDCF]
//             | [#xFDF0-#xFFFD]
//             | [#x10000-#xEFFFF]
const isNameStartCode = cc =>
  (cc >= 0x41 && cc <= 0x5a) || // A-Z
  cc === 0x5f || // _
  (cc >= 0x61 && cc <= 0x7a) || // a-z
  (cc >= 0xc0 && cc <= 0xd6) || // À-Ö
  (cc >= 0xd8 && cc <= 0xf6) || // Ø-ö
  (cc >= 0xf8 && cc <= 0x2ff) ||
  (cc >= 0x370 && cc <= 0x37d) ||
  (cc >= 0x37f && cc <= 0x1fff) ||
  (cc >= 0x200c && cc <= 0x200d) ||
  (cc >= 0x2070 && cc <= 0x2187) ||
  (cc >= 0x2c00 && cc <= 0x2fef) ||
  (cc >= 0x3001 && cc <= 0xd7ff) ||
  (cc >= 0xf900 && cc <= 0xfdcf) ||
  (cc >= 0xfdf0 && cc <= 0xfffd) ||
  (cc >= 0x10000 && cc <= 0xeffff)

// NameChar ::= NameStart | [0-9] | "-" | "." | #xB7
//            | [#x0300-#x036F] | [#x203F-#x2040]
const isNameCharCode = cc =>
  isNameStartCode(cc) ||
  cc === 0x2d || // -
  cc === 0x2e || // .
  (cc >= 0x30 && cc <= 0x39) || // 0-9
  cc === 0xb7 || // ·
  (cc >= 0x300 && cc <= 0x36f) ||
  cc === 0x203f || // ‿
  cc === 0x2040 // ⁀

export function isValidNmtoken (str) {
  for (let i = 0; i < str.length; ++i) {
    const cc = str.charCodeAt(i)
    if (!isNameCharCode(cc)) return false
  }
  return str.length > 0
}

// Name ::= NameStart NameChar* /* ws: explicit */
/**
 *
 * @param {string} src
 * @param {number} start
 * @returns {string}
 */
export function parseNameValue (src, start) {
  if (!isNameStartCode(src.charCodeAt(start))) return ''
  let pos = start + 1
  while (isNameCharCode(src.charCodeAt(pos))) pos += 1
  return src.substring(start, pos)
}

// Nmtoken ::= NameChar+ /* ws: explicit */
/**
 *
 * @param {string} src
 * @param {number} start
 * @param {MessageSyntaxError[]} errors
 * @returns {NmtokenParsed}
 */
export function parseNmtoken (src, start, errors) {
  let pos = start
  while (isNameCharCode(src.charCodeAt(pos))) pos += 1
  const value = src.substring(start, pos)
  if (!value) {
    errors.push(new MessageSyntaxError('empty-token', start, start + 1))
  }
  return { type: 'nmtoken', start, end: pos, value }
}

// WHITESPACES
// WhiteSpace ::= #x9 | #xD | #xA | #x20 /* ws: definition */
/**
 *
 * @param {string} src
 * @param {number} start
 * @returns {number}
 */
function whitespaces (src, start) {
  let length = 0
  let ch = src[start]
  while (ch === '\t' || ch === '\n' || ch === '\r' || ch === ' ') {
    length += 1
    ch = src[start + length]
  }
  return length
}

/*
const mf = new MessageFormat(
  (
    'match {$count :plural}' +
    ' when 1 {You have one notification.}' +
    ' when one {You have {$count} notification.}' +
    ' when * {You have {$count} notifications.}'
  ),
  'en-US'
)

// skeleton=yMMMdE
const dmf = new MessageFormat('{Todays Date: {$now :datetime dateStyle=medium timeStyle=short}}', 'en-US')

console.log(mf.resolveMessage({ count: 1 }))
console.log(mf.resolveMessage({ count: 8 }))
console.log(dmf.resolveMessage({ now: new Date() }))
console.log(dmf.resolveMessage({ now: new Date() }).toString())
console.log(dmf.resolvedOptions())
*/
